import { reconcileReportList, mergeReportDataRecords } from "../../utils/reportIdentity";
import { canonicalEntityId, isTemporaryEntityId, createReportReadGuard } from "../../lib/syncedEntityIds";
import { mergeFileRecords } from "../../utils/fileIdentity";
import { mergeSiteData } from "../../utils/mergeSiteData";
import { errorMessage } from "../../utils/mutationResult.js";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import auditReportService from "../../services/auditReportService.js";
// Import other thunks so we can listen to their fulfilled actions and keep
// auditReports.currentReportData/allReportsData in sync when other slices
// update (files, works, observations, site overview)
import { uploadFile, uploadMultipleFiles, updateFile, deleteFile, cloneFiles, addSyncedFile, hydrateOfflineFile } from "../slices/fileSlice";
import { createWork, updateWork, deleteWork } from "../slices/workSlice";
import { createObservation, updateObservation, deleteObservation, fetchObservations, updateObservationWithRiskMatrix, addFollowUpAction, updateFollowUpAction, resolveObservation } from "../slices/observationSlice";
import { updateSiteData } from "../slices/siteDataSlice";
import { updateSiteOverviewData } from "../slices/siteOverviewDataSlice";
import { getCachedData, putCachedData, putOutboxItem } from "../../lib/offlineDb.js";
import { upsertOutboxItem } from "./offlineSlice.js";
import { mutateKeyIssueMatrix, applySyncedKeyIssue } from "./keyIssueMatrixSlice";
import { normalizeReportMatrix, withoutKeyIssues, mergePendingMatrix } from "../../utils/keyIssueMatrix";
import { reconcileObservationUpdate } from "../../utils/observationIdentity";
import { warmImageCache } from "../../lib/imageCache";


const initialState = {
  recentReports: [],
  siteReports: [],
  currentReport: null,
  allReportsData: {}, // { reportId: { siteOverview, works, observations, files, report } }
  currentReportData: null, // Current opened report with all its data
  pendingFileChangesByReportId: {},
  pendingFilePersistenceByReportId: {},
  loading: false,
  error: null,
  dataLoading: false, // Loading for detailed report data
};

const normalizeReport = (report) => report || null;
const knownReports = (state) => [
  ...Object.values(state?.allReportsData || {}).map(record => record?.report),
  ...(state?.recentReports || []), ...(state?.siteReports || []), state?.currentReport,
].filter(Boolean);
const canonicalReport = (state, report) => report
  ? reconcileReportList([report], knownReports(state))[0] : null;
const offlineReportSnapshot = async (getState, cached) => {
  const savedIds = await getCachedData("sync:resolved-ids").catch(() => null);
  const state = getState();
  return mergeReportDataRecords(cached, Object.values(state.auditReports?.allReportsData || {}),
    { ...savedIds, ...state.offline?.resolvedIds });
};

const syncCurrentReportData = (state) => {
  if (state.currentReport?._id) {
    const canonical = canonicalReport(state, state.currentReport);
    if (canonical?._id !== state.currentReport._id || (!state.currentReport._clientId && canonical?._clientId)) {
      state.currentReport = canonical;
    }
    state.currentReportData = state.allReportsData?.[state.currentReport._id] || null;
  } else {
    state.currentReportData = null;
  }
};

// Update a parent's ID and its image relations in the same Redux action.
// Progress must never see a server parent paired with temporary image links.
const reconcileSyncedReportEntity = (record, field, entityType, incoming, clientId) => {
  const aliases = new Set([incoming._id, incoming.id, clientId].filter(Boolean));
  const matches = (item) => [item._id, item.id, item._clientId].some(id => aliases.has(id));
  for (const item of (record[field] || []).filter(matches)) {
    for (const id of [item._id, item.id, item._clientId]) if (id) aliases.add(id);
  }
  record[field] = reconcileObservationUpdate(record[field] || [], {
    ...incoming, _id: incoming._id, id: incoming._id,
    ...(clientId ? { _clientId: clientId } : {}),
  }, clientId);
  record.files = (record.files || []).map(file =>
    file.relatedTo?.entityType === entityType && aliases.has(file.relatedTo.entityId)
      ? { ...file, relatedTo: { ...file.relatedTo, entityId: incoming._id } }
      : file);
};

const getActiveReportId = () =>
  window.sessionStorage?.getItem("activeReportId") || window.localStorage?.getItem("activeReportId") || "";

const ensureReportData = (state, reportId) => {
  if (!reportId) return null;
  reportId = canonicalReport(state, { _id: reportId })?._id || reportId;
  if (!state.allReportsData) state.allReportsData = {};
  if (!state.allReportsData[reportId]) {
    state.allReportsData[reportId] = {
      report: null,
      siteOverview: null,
      works: [],
      keyIssueMatrixItems: [],
      observations: [],
      files: [],
    };
  }
  return state.allReportsData[reportId];
};

const getFileImportKey = (file) =>
  [
    file?.sourceFileId || file?.metadata?.importedFromFileId || file?._id || file?.id,
    file?.metadata?.importScope || "section",
    file?.relatedTo?.entityType || "",
    file?.relatedTo?.entityId || "",
    JSON.stringify(file?.relatedTo?.filterData || {}),
  ].join("|");

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchRecentWorkingReports = createAsyncThunk(
  "auditReports/fetchRecentWorkingReports",
  async (_, { rejectWithValue, getState }) => {
    const cacheKey = "audit-reports:recent";
    const reconcile = async (cached) => {
      const savedIds = await getCachedData("sync:resolved-ids").catch(() => null);
      return reconcileReportList(cached, knownReports(getState().auditReports),
        { ...savedIds, ...getState().offline?.resolvedIds });
    };
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const cached = await getCachedData(cacheKey);
        if (cached) return reconcile(cached);
      }
      const response = await auditReportService.getRecentWorkingReports();
      const reports = reconcileReportList(response?.data?.data?.reports || [], knownReports(getState().auditReports), getState().offline?.resolvedIds);
      await putCachedData(cacheKey, reports);
      return reports;
    } catch (error) {
      const cached = await getCachedData(cacheKey).catch(() => null);
      if (cached) return reconcile(cached);
      return rejectWithValue(errorMessage(error, "Failed to fetch recent reports"));
    }

  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const fetchReportsBySite = createAsyncThunk(
  "auditReports/fetchReportsBySite",
  async ({ siteId, status = "all" }, { rejectWithValue }) => {
    try {
      const response = await auditReportService.getReportsBySite(siteId, status);
      const reports = response?.data?.data?.reports || [];
      return reports;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to fetch site reports"));
    }
  },
);

function makeReportClientId() {
  return `report_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`
}

// Reports created while offline don't have a real Mongo `_id` yet. Anything
// created against this report while it's still a placeholder (works,
// observations, files) is queued using this id as `reportId`; `syncManager`
// swaps every occurrence of it for the real id once the report itself syncs.
export const buildOfflineReportId = (clientId) => `offline-report-${clientId}`
export const isOfflineReportId = (id) => typeof id === "string" && id.startsWith("offline-report-")

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const createReport = createAsyncThunk(
  "auditReports/createReport",
  async ({ siteId, templateId, templateName, reportName, projectId }, { rejectWithValue, dispatch }) => {
    const knownOffline = typeof navigator !== "undefined" && navigator.onLine === false

    if (!knownOffline) {
      try {
        const res = await auditReportService.createReport({ siteId, templateId, templateName, reportName, projectId });
        const report = res?.data?.data?.auditReport || res?.data?.auditReport || res?.data?.data;
        return { report, queued: false };
      } catch (error) {
        const isNetworkFailure = error?.name === "TypeError" || !error?.response;
        if (!isNetworkFailure) {
          return rejectWithValue(errorMessage(error, "Failed to create report"));
        }
        // fall through to offline queueing below
      }
    }

    const clientId = makeReportClientId();
    const item = await putOutboxItem({
      id: `outbox_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
      type: "report-create",
      siteId,
      templateId,
      templateName,
      reportName,
      projectId,
      clientId,
      meta: { description: `Create report "${reportName}"`, entityType: "report", entityId: clientId },
    });
    if (dispatch) dispatch(upsertOutboxItem(item));

    const placeholderReport = {
      _id: buildOfflineReportId(clientId),
      _clientId: clientId,
      siteId,
      templateId,
      templateName,
      reportName,
      status: "not-started",
      completionPercentage: 0,
      _offlineQueued: true,
      createdAt: new Date().toISOString(),
    };
    if (dispatch) dispatch({ type: "auditReports/addOptimisticReport", payload: placeholderReport });

    // Persist the optimistic report in the same caches used by the dashboard
    // offline loaders, so a navigation/reload still shows it in Recent audits.
    const cachedRecent = await getCachedData("audit-reports:recent").catch(() => []);
    const recentReports = [
      placeholderReport,
      ...(Array.isArray(cachedRecent) ? cachedRecent : []).filter(
        (item) => item?._id !== placeholderReport._id && item?._clientId !== clientId,
      ),
    ];
    await putCachedData("audit-reports:recent", recentReports);
    const cachedAll = await getCachedData("audit-reports:all-with-data").catch(() => []);
    const allReports = [
      { report: placeholderReport, siteOverview: null, works: [], observations: [], files: [] },
      ...(Array.isArray(cachedAll) ? cachedAll : []).filter(
        (item) => item?.report?._id !== placeholderReport._id && item?.report?._clientId !== clientId,
      ),
    ];
    await putCachedData("audit-reports:all-with-data", allReports);

    return { report: placeholderReport, queued: true };
  },
);

// Temporary reports are local records even after connectivity returns. Their
// parent site can finish syncing before report creation gets a server ID.
const readLocalReport = async (state, reportId) => {
  const record = state?.auditReports?.allReportsData?.[reportId];
  if (record?.report) return record;
  const cached = await getCachedData("audit-reports:all-with-data");
  return (Array.isArray(cached) ? cached : []).find(entry => entry?.report?._id === reportId);
};

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, string, {}>} */
export const fetchReportById = createAsyncThunk(
  "auditReports/fetchReportById",
  async (reportId, { rejectWithValue, getState }) => {
    try {
      reportId = canonicalEntityId(getState(), "report", reportId);
      if (isTemporaryEntityId(reportId)) {
        const record = await readLocalReport(getState(), reportId);
        if (record?.report) return normalizeReport(record.report);
        return rejectWithValue("This report is waiting to sync. Its local data is unavailable.");
      }
      const response = await auditReportService.getReport(reportId);
      const report = response?.data?.data || response?.data?.auditReport || response?.data;
      return normalizeReport(report);
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to fetch report"));
    }
  },
);

/**
 * Fetch complete report data with all related information
 * Returns: { report, siteOverview, works, observations, files }
 */
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, { reportId: string; siteId?: string }, {}>} */
export const fetchReportWithAllData = createAsyncThunk(
  "auditReports/fetchReportWithAllData",
  async ({ reportId, siteId }, { rejectWithValue, getState }) => {
    try {
      reportId = canonicalEntityId(getState(), "report", reportId);
      siteId = canonicalEntityId(getState(), "site", siteId);
      if (isTemporaryEntityId(reportId) || isTemporaryEntityId(siteId)) {
        const record = await readLocalReport(getState(), reportId);
        if (record) return record;
        return rejectWithValue("This report is waiting to sync. Its local data is unavailable.");
      }
      // A newly assigned server ID does not mean its queued sections have
      // reached the server. Keep reading the optimistic report during sync.
      const localDuringSync = createReportReadGuard(getState, reportId);
      const local = localDuringSync();
      if (local?.report) return local;
      const response = await auditReportService.getReportWithAllData(reportId, siteId);
      const data = response?.data?.data || response?.data;
      await warmImageCache(data?.files || []);
      // Ignore pre-sync responses even if they arrive after the final refresh.
      return localDuringSync() || data;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to fetch report with data"));
    }
  },
);

/**
 * Fetch all recent reports with their complete data for dashboard progress tracking
 * Returns: Array of reports with all their related data
 */
/** @type {import("@reduxjs/toolkit").AsyncThunk<any, void, {}>} */
export const fetchAllReportsWithData = createAsyncThunk(
  "auditReports/fetchAllReportsWithData",
  async (_, { rejectWithValue, getState }) => {
    const cacheKey = "audit-reports:all-with-data";
    try {
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        const cached = await getCachedData(cacheKey);
        if (cached) return offlineReportSnapshot(getState, cached);
      }
      const response = await auditReportService.getAllReportsWithData();
      const data = (response?.data?.data || response?.data || []).map((record) =>
        mergePendingMatrix(record, getState()?.auditReports?.allReportsData?.[record.report?._id]));
      await putCachedData(cacheKey, data);
      warmImageCache(data.flatMap((record) => record.files || [])).catch(() => {});
      return data; // Array of { report, siteOverview, works, observations, files }
    } catch (error) {
      const cached = await getCachedData(cacheKey).catch(() => null);
      if (cached) return offlineReportSnapshot(getState, cached);
      return rejectWithValue(errorMessage(error, "Failed to fetch all reports with data"));
    }

  },
);

/** @type {import("@reduxjs/toolkit").AsyncThunk<any, object, {}>} */
export const persistPendingFilesForReport = createAsyncThunk(
  "auditReports/persistPendingFilesForReport",
  async ({ reportId = null, siteId = null } = {}, { dispatch, getState, rejectWithValue }) => {
    // Module-level in-flight guard to avoid concurrent persists for same report
    try {
      const state = getState();
      const rid = reportId || getActiveReportId();
      if (!rid) return null;

      // Initialize in-flight set on module if missing
      if (!global.__persistPendingFilesInFlight) global.__persistPendingFilesInFlight = new Set();
      const inFlightSet = global.__persistPendingFilesInFlight;

      if (inFlightSet.has(rid)) {
        // Another persistence already running for this report; skip
        return [];
      }

      inFlightSet.add(rid);
      const reportData = state?.auditReports?.allReportsData?.[rid] || state?.auditReports?.currentReportData || null;
      if (!reportData) return null;

      const pendingFiles = (reportData.files || []).filter(
        (f) =>
          f?.isImported &&
          f?.metadata?.importScope === "section" &&
          !String(f?.relatedTo?.entityId || "").startsWith("imported-"),
      );
      if (!pendingFiles || pendingFiles.length === 0) return [];

      const resolvedSiteId = siteId || pendingFiles[0]?.siteId || "";

      // Dispatch cloneFiles which will replace imported placeholders with real DB files
      const res = await dispatch(cloneFiles({ files: pendingFiles, siteId: resolvedSiteId, reportId: rid }));
      // Return cloned files payload if available
      return res?.payload || res;
    } catch (error) {
      return rejectWithValue(errorMessage(error, "Failed to persist pending files"));
    } finally {
      try {
        if (global.__persistPendingFilesInFlight) global.__persistPendingFilesInFlight.delete(reportId || (typeof window !== 'undefined' && (window.sessionStorage?.getItem('activeReportId') || window.localStorage?.getItem('activeReportId'))) || '');
      } catch (e) {
        // noop
      }
    }
  },
  {
    condition: ({ reportId = null } = {}, { getState }) => {
      const rid = reportId || (typeof window !== 'undefined' && (window.sessionStorage?.getItem('activeReportId') || window.localStorage?.getItem('activeReportId'))) || '';
      const persisting = getState()?.auditReports?.pendingFilePersistenceByReportId?.[rid];
      return !Boolean(persisting);
    },
  },
);

const auditReportSlice = createSlice({
  name: "auditReports",
  initialState,
  reducers: {
    setRecentReports: (state, action) => {
      state.recentReports = reconcileReportList(action.payload || [], knownReports(state));
    },
    setSiteReports: (state, action) => {
      state.siteReports = reconcileReportList(action.payload || [], knownReports(state));
    },
    setCurrentReport: (state, action) => {
      state.currentReport = canonicalReport(state, normalizeReport(action.payload));
      syncCurrentReportData(state);
    },
    setCurrentReportData: (state, action) => {
      if (!action.payload) { state.currentReportData = null; return; }
      const reportId = action.payload.report?._id;
      const previous = state.allReportsData[reportId] ||
        (state.currentReportData?.report?._id === reportId ? state.currentReportData : null);
      const record = mergePendingMatrix(action.payload, previous);
      // The section hydration effect compares these references. Keep a single
      // canonical record so syncing the current view settles after one dispatch.
      if (reportId) {
        state.allReportsData[reportId] = record;
        state.currentReportData = state.allReportsData[reportId];
      } else state.currentReportData = record;
    },
    updateCurrentReportFile: (state, action) => {
      const { fileId, fileData } = action.payload || {};
      if (!fileId || !fileData || !state.currentReportData?.files) return;
      state.currentReportData.files = state.currentReportData.files.map((file) =>
        String(file?.id || file?._id || file?._clientId || "") === String(fileId)
          ? { ...file, ...fileData }
          : file,
      );
    },
    // The observation/work screens render images from `currentReportData.files`,
    // not the standalone files slice. Without this, a synced file's pending_*
    // placeholder id lingers here after sync, so a later edit to that same
    // image (description, delete) targets an id the server never issued.
    replaceSyncedReportFile: (state, action) => {
      const { file, clientId } = action.payload || {};
      if (!file) return;
      const reportId = file.reportId || getActiveReportId();
      const syncedFile = clientId ? { ...file, _clientId: clientId } : file;
      Object.entries(state.allReportsData || {}).forEach(([rid, record]) => {
        if (rid === reportId || record.files?.some(existing => clientId && existing._clientId === clientId)) {
          record.files = mergeFileRecords(record.files, [syncedFile]);
        }
      });
      syncCurrentReportData(state);
    },
    setReportData: (state, action) => {
      const record = action.payload ? mergePendingMatrix(action.payload, state.allReportsData[action.payload.report?._id]) : null;
      if (!record?.report?._id) return;
      const previous = state.allReportsData[record.report._id] || {};
      state.allReportsData[record.report._id] = {
        ...previous,
        ...record,
        report: { ...previous.report, ...record.report },
        siteOverview:
          record.siteOverview || record.siteOverviewData || record.siteData || previous.siteOverview || null,
        works: Array.isArray(record.works) ? record.works : previous.works || [],
        observations: Array.isArray(record.observations) ? record.observations : previous.observations || [],
        files: Array.isArray(record.files) ? record.files : previous.files || [],
      };
      state.currentReport = state.allReportsData[record.report._id].report;
      syncCurrentReportData(state);
    },
    clearCurrentReport: (state) => {
      state.currentReport = null;
      state.currentReportData = null;
    },
    upsertReport: (state, action) => {
      const report = normalizeReport(action.payload);
      if (!report?._id) return;

      const existingIndex = state.recentReports.findIndex((item) => item._id === report._id);
      if (existingIndex >= 0) {
        state.recentReports[existingIndex] = { ...state.recentReports[existingIndex], ...report };
      } else {
        state.recentReports = [report, ...state.recentReports];
      }

      const siteIndex = state.siteReports.findIndex((item) => item._id === report._id);
      if (siteIndex >= 0) {
        state.siteReports[siteIndex] = { ...state.siteReports[siteIndex], ...report };
      } else {
        state.siteReports = [report, ...state.siteReports];
      }

      if (state.currentReport?._id === report._id) {
        state.currentReport = { ...state.currentReport, ...report };
      }
    },
    // Reconciliation: replace the optimistic offline placeholder report
    // (matched by `_clientId`) with the real, server-assigned report once
    // `syncManager` has successfully replayed the queued `report-create`
    // outbox item.
    addOptimisticReport: (state, action) => {
      const report = action.payload;
      if (!report?._id) return;
      const sameReport = (item) =>
        item?._id === report._id ||
        (report._clientId && item?._clientId === report._clientId);
      state.recentReports = [report, ...state.recentReports.filter((item) => !sameReport(item))];
      state.siteReports = [report, ...state.siteReports.filter((item) => !sameReport(item))];
      state.currentReport = report;
      ensureReportData(state, report._id).report = report;
      syncCurrentReportData(state);
    },
    addSyncedReport: (state, action) => {
      const { report, clientId } = action.payload || {};
      if (!report?._id) return;

      // Keep `_clientId` on the reconciled record so any component (e.g.
      // useResolvedAuditRouteIds) that still needs to match the old
      // offline placeholder id against this now-synced report can do so on
      // the very same render the sync completes, not only after a remount.
      const syncedReport = clientId ? { ...report, _clientId: clientId } : report;

      const recentIndex = state.recentReports.findIndex((item) => item._clientId === clientId);
      if (recentIndex >= 0) {
        state.recentReports[recentIndex] = syncedReport;
      } else {
        state.recentReports = [syncedReport, ...state.recentReports];
      }

      const siteIndex = state.siteReports.findIndex((item) => item._clientId === clientId);
      if (siteIndex >= 0) {
        state.siteReports[siteIndex] = syncedReport;
      } else {
        state.siteReports = [syncedReport, ...state.siteReports];
      }

      const placeholderId = buildOfflineReportId(clientId);
      if (state.allReportsData[placeholderId]) {
        state.allReportsData[report._id] = { ...state.allReportsData[placeholderId], report: syncedReport };
        const record = state.allReportsData[report._id];
        for (const key of ["observations", "works", "files", "keyIssueMatrixItems"]) {
          record[key] = (record[key] || []).map((item) => ({ ...item, reportId: report._id, siteId: report.siteId || item.siteId }));
        }
        record.pendingKeyIssueChanges = (record.pendingKeyIssueChanges || []).map((change) => ({ ...change,
          item: { ...change.item, reportId: report._id, siteId: report.siteId || change.item.siteId } }));
        delete state.allReportsData[placeholderId];
      }
      if (state.currentReport?._clientId === clientId) {
        state.currentReport = syncedReport;
        syncCurrentReportData(state);
      }
    },
    removeReport: (state, action) => {
      const reportId = action.payload;
      state.recentReports = state.recentReports.filter((item) => item._id !== reportId);
      state.siteReports = state.siteReports.filter((item) => item._id !== reportId);
      if (state.currentReport?._id === reportId) {
        state.currentReport = null;
        state.currentReportData = null;
      }
      delete state.allReportsData[reportId];
    },
    addReportData: (state, action) => {
      const { reportId, data } = action.payload;
      state.allReportsData[reportId] = normalizeReportMatrix(data);
      if (state.currentReport?._id === reportId) {
        state.currentReportData = state.allReportsData[reportId];
      }
    },
    addPendingImportedFilesToReport: (state, action) => {
      const { reportId = getActiveReportId(), files = [] } = action.payload || {};
      const reportData = ensureReportData(state, reportId);
      if (!reportData || !Array.isArray(files) || files.length === 0) return;

      const existingImportKeys = new Set(
        (reportData.files || []).map((file) => getFileImportKey(file)),
      );
      const nextFiles = files.filter((file) => {
        const importKey = getFileImportKey(file);
        if (!file?.sourceFileId && !file?.metadata?.importedFromFileId) return false;
        if (existingImportKeys.has(importKey)) return false;
        existingImportKeys.add(importKey);
        return true;
      });

      reportData.files = [...nextFiles, ...(reportData.files || [])];
      if (nextFiles.length > 0) {
        state.pendingFileChangesByReportId[reportId] = true;
      }
      syncCurrentReportData(state);
    },
    removePendingImportedFileFromReport: (state, action) => {
      const { reportId = getActiveReportId(), fileId } = action.payload || {};
      const reportData = ensureReportData(state, reportId);
      if (!reportData || !fileId) return;

      const beforeCount = reportData.files?.length || 0;
      reportData.files = (reportData.files || []).filter(
        (file) => (file?._id || file?.id) !== fileId,
      );
      if ((reportData.files?.length || 0) !== beforeCount) {
        state.pendingFileChangesByReportId[reportId] = true;
      }
      syncCurrentReportData(state);
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase("works/addSyncedWork", (state, { payload }) => {
        const { work, clientId } = payload || {};
        if (!work?._id || !work.reportId) return;
        const record = ensureReportData(state, work.reportId);
        reconcileSyncedReportEntity(record, "works", "work", work, clientId);
        syncCurrentReportData(state);
      })
      .addCase("observations/addSyncedObservation", (state, { payload }) => {
        const observation = withoutKeyIssues(payload.observation);
        if (!observation?._id) return;
        const record = ensureReportData(state, observation.reportId);
        reconcileSyncedReportEntity(record, "observations", "observation", observation, payload.clientId);
        record.keyIssueMatrixItems = (record.keyIssueMatrixItems || []).map((item) =>
          item.observationId === payload.clientId ? { ...item, observationId: observation._id } : item);
        record.pendingKeyIssueChanges = (record.pendingKeyIssueChanges || []).map((change) => ({ ...change,
          item: change.item.observationId === payload.clientId ? { ...change.item, observationId: observation._id } : change.item }));
        syncCurrentReportData(state);
      })
      .addCase(applySyncedKeyIssue, (state, { payload }) => {
        const { item, previousReportId } = payload;
        if (previousReportId && previousReportId !== String(item.reportId) && state.allReportsData[previousReportId]) {
          state.allReportsData[previousReportId].keyIssueMatrixItems = (state.allReportsData[previousReportId].keyIssueMatrixItems || [])
            .filter((entry) => entry._id !== item._id);
        }
        const record = ensureReportData(state, item.reportId);
        if (payload.preservePending) {
          const pending = (record.pendingKeyIssueChanges || []).find((change) => change.item._id === item._id);
          if (pending) {
            pending.item = { ...pending.item, observationId: item.observationId, reportId: item.reportId, _offlineQueued: false };
            record.keyIssueMatrixItems = (record.keyIssueMatrixItems || []).filter((entry) => entry._id !== item._id);
            if (pending.action !== "delete") record.keyIssueMatrixItems.push(pending.item);
            syncCurrentReportData(state);
            return;
          }
        }
        record.pendingKeyIssueChanges = (record.pendingKeyIssueChanges || []).filter((change) => change.item._id !== item._id);
        record.keyIssueMatrixItems = (record.keyIssueMatrixItems || []).filter((entry) => entry._id !== item._id);
        if (item.isActive !== false) record.keyIssueMatrixItems.push(item);
        syncCurrentReportData(state);
      })
      .addCase(mutateKeyIssueMatrix.fulfilled, (state, { payload }) => {
        const { item, action } = payload;
        const record = ensureReportData(state, item.reportId);
        if (!record) return;
        record.pendingKeyIssueChanges = (record.pendingKeyIssueChanges || []).filter((change) => change.item._id !== item._id);
        if (payload.queued) record.pendingKeyIssueChanges.push({ item, action });
        record.keyIssueMatrixItems = (record.keyIssueMatrixItems || []).filter((entry) => entry._id !== item._id);
        if (action !== "delete") record.keyIssueMatrixItems.push(item);
        if (action === "delete") record.files = (record.files || []).filter((file) =>
          file?.relatedTo?.entityType !== "key-issue-matrix" || String(file.relatedTo.entityId) !== String(item._id));
        syncCurrentReportData(state);
      })
      .addCase(fetchRecentWorkingReports.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRecentWorkingReports.fulfilled, (state, action) => {
        state.loading = false;
        state.recentReports = reconcileReportList(action.payload || [], knownReports(state));
      })
      .addCase(fetchRecentWorkingReports.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchReportsBySite.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportsBySite.fulfilled, (state, action) => {
        state.loading = false;
        state.siteReports = reconcileReportList(action.payload || [], knownReports(state));
      })
      .addCase(fetchReportsBySite.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchReportById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReportById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentReport = canonicalReport(state, action.payload);
        syncCurrentReportData(state);
      })
      .addCase(fetchReportById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Handle fetchReportWithAllData
      .addCase(fetchReportWithAllData.pending, (state) => {
        state.dataLoading = true;
        state.error = null;
      })
      .addCase(fetchReportWithAllData.fulfilled, (state, action) => {
        state.dataLoading = false;
        const data = action.payload;
        const records = (Array.isArray(data) ? data : [data]).map((record) =>
          mergePendingMatrix(record, state.allReportsData[record?.report?._id]));
        records.forEach((record) => {
          if (!record?.report?._id) return;
          const reportId = record.report._id;
          const previous = state.allReportsData[reportId] || {};
          state.allReportsData[reportId] = {
            ...previous,
            ...record,
            report: { ...previous.report, ...record.report },
            siteOverview:
              record.siteOverview || record.siteOverviewData || record.siteData || previous.siteOverview || null,
            works: Array.isArray(record.works) ? record.works : previous.works || [],
            observations: Array.isArray(record.observations) ? record.observations : previous.observations || [],
            files: Array.isArray(record.files) ? record.files : previous.files || [],
          };
        });
        syncCurrentReportData(state);
      })
      // Keep auditReports data in sync when other slices mutate data
      .addCase(uploadFile.fulfilled, (state, action) => {
        const file = action.payload;
        const reportId = file?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) {
          reportData.files = mergeFileRecords(reportData.files, [file]);
        }
        syncCurrentReportData(state);
      })
      .addCase(uploadMultipleFiles.fulfilled, (state, action) => {
        const files = action.payload || [];
        const first = files[0] || {};
        const reportId = first?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) {
          reportData.files = mergeFileRecords(reportData.files, files);
        }
        syncCurrentReportData(state);
      })
      .addCase(updateFile.fulfilled, (state, action) => {
        const file = action.payload;
        const reportId = file?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) {
          reportData.files = (reportData.files || []).map((f) => (f._id === file._id ? file : f));
        }
        syncCurrentReportData(state);
      })
      .addCase(cloneFiles.fulfilled, (state, action) => {
        const clonedFiles = action.payload || [];
        const first = clonedFiles[0] || {};
        const reportId = first?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);

        if (reportData) {
          reportData.files = (reportData.files || []).filter((existing) => {
            if (!existing?.isImported) return true;
            return !clonedFiles.some((cloned) => {
              const importedFromFileId = cloned?.metadata?.importedFromFileId;
              const sameSource =
                String(existing?.sourceFileId || "") ===
                String(importedFromFileId || "");
              const sameEntity =
                existing?.relatedTo?.entityType === cloned?.relatedTo?.entityType &&
                (String(existing?.relatedTo?.entityId || "") ===
                  String(cloned?.relatedTo?.entityId || "") ||
                  String(existing?.relatedTo?.entityId || "").startsWith("imported-"));
              const sameFilter =
                JSON.stringify(existing?.relatedTo?.filterData || {}) ===
                JSON.stringify(cloned?.relatedTo?.filterData || {});
              return sameSource && sameEntity && sameFilter;
            });
          });
          reportData.files = [...clonedFiles, ...(reportData.files || [])];
          const hasPendingImportedFiles = (reportData.files || []).some(
            (file) => file?.isImported,
          );
          if (!hasPendingImportedFiles) {
            delete state.pendingFileChangesByReportId[reportId];
          }
        }
        syncCurrentReportData(state);
      })
      // Phase 2: a queued offline upload synced during a manual sync — swap
      // the placeholder (matched by `_clientId`) for the real DB record so
      // reportData.files (what the audit UI actually renders from) reflects
      // it, mirroring the same fix applied to fileSlice.js.
      .addCase(addSyncedFile, (state, action) => {
        const { file, clientId } = action.payload || {};
        if (!file) return;
        const reportId = file?.reportId || getActiveReportId();
        const syncedFile = clientId ? { ...file, _clientId: clientId } : file;
        Object.entries(state.allReportsData || {}).forEach(([rid, rd]) => {
          if (rid === reportId || rd.files?.some(existing => clientId && existing._clientId === clientId)) {
            rd.files = mergeFileRecords(rd.files, [syncedFile]);
          }
        });
      syncCurrentReportData(state);
      })
      // Phase 3: rebuild a still-queued offline upload's placeholder into
      // the report it belongs to after a page reload (see fileSlice.js).
      // Retain uploads even if hydration finishes before report loading,
      // and replace expired cached preview URLs on existing placeholders.
      .addCase(hydrateOfflineFile, (state, action) => {
        const placeholder = action.payload;
        if (!placeholder) return;
        const reportId = placeholder?.reportId;
        const rd = reportId ? ensureReportData(state, reportId) : null;
        if (!rd?.files) return;
        const index = rd.files.findIndex((f) => f._clientId === placeholder._clientId);
        if (index === -1) {
          rd.files = [placeholder, ...rd.files];
        } else if (rd.files[index]._offlineQueued) {
          const existing = rd.files[index];
          rd.files[index] = { ...existing, ...placeholder, fileDescription: existing.fileDescription || placeholder.fileDescription };
        }
        syncCurrentReportData(state);
      })
      .addCase(deleteFile.fulfilled, (state, action) => {
        const fileId = action.payload;
        // Remove file from any reportData that contains it
        Object.keys(state.allReportsData || {}).forEach((rid) => {
          const rd = state.allReportsData[rid];
          if (rd?.files && rd.files.some((f) => f._id === fileId)) {
            rd.files = rd.files.filter((f) => f._id !== fileId);
          }
        });
        syncCurrentReportData(state);
      })
      .addCase(createWork.fulfilled, (state, action) => {
        const work = action.payload;
        const reportId = work?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.works = [work, ...(reportData.works || [])];
        syncCurrentReportData(state);
      })
      .addCase(updateWork.fulfilled, (state, action) => {
        const work = action.payload;
        const reportId = work?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.works = (reportData.works || []).map((w) => (w._id === work._id ? work : w));
        syncCurrentReportData(state);
      })
      .addCase(deleteWork.fulfilled, (state, action) => {
        const workId = action.payload;
        Object.keys(state.allReportsData || {}).forEach((rid) => {
          const rd = state.allReportsData[rid];
          if (rd?.works && rd.works.some((w) => w._id === workId)) {
            rd.works = rd.works.filter((w) => w._id !== workId);
          }
        });
        syncCurrentReportData(state);
      })
      .addCase(createObservation.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        const reportId = obs?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.observations = [obs, ...(reportData.observations || [])];
        syncCurrentReportData(state);
      })
      .addCase(updateObservation.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        if (!obs) return;
        const requestedId = action.meta?.arg?.observationId;
        const reportId = obs?.reportId || action.meta?.arg?.observationData?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) {
          reportData.observations = reconcileObservationUpdate(reportData.observations, obs, requestedId);
        }
        syncCurrentReportData(state);
      })
      // Also handle updates coming from the risk-matrix update thunk
      .addCase(updateObservationWithRiskMatrix.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        const reportId = obs?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.observations = (reportData.observations || []).map((o) => (o._id === obs._id ? obs : o));
        syncCurrentReportData(state);
      })
      .addCase(deleteObservation.fulfilled, (state, action) => {
        const obsId = action.payload;
        Object.keys(state.allReportsData || {}).forEach((rid) => {
          const rd = state.allReportsData[rid];
          if (rd?.observations && rd.observations.some((o) => o._id === obsId)) {
            rd.observations = rd.observations.filter((o) => o._id !== obsId);
          }
          const removed = (rd.keyIssueMatrixItems || []).filter((item) => item.observationId === obsId);
          const removedIds = new Set(removed.map((item) => String(item._id)));
          rd.pendingKeyIssueChanges = (rd.pendingKeyIssueChanges || []).filter((change) => !removedIds.has(String(change.item._id)));
          rd.pendingKeyIssueChanges.push(...removed.map((item) => ({ item, action: "delete" })));
          rd.keyIssueMatrixItems = (rd.keyIssueMatrixItems || []).filter((item) => item.observationId !== obsId);
          rd.files = (rd.files || []).filter((file) => file?.relatedTo?.entityType !== "key-issue-matrix" || !removedIds.has(String(file.relatedTo.entityId)));
        });
        syncCurrentReportData(state);
      })
      // When observations are fetched (list), store them against the report cache if reportId provided
      .addCase(fetchObservations.fulfilled, (state, action) => {
        try {
          const params = (action.meta && action.meta.arg) || {};
          const reportId = params?.reportId || getActiveReportId();
          const reportData = ensureReportData(state, reportId);
          const observations = action.payload?.observations || action.payload || [];
          if (reportData) reportData.observations = observations;
          syncCurrentReportData(state);
        } catch (e) {
          // noop
        }
      })
      .addCase(addFollowUpAction.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        const reportId = obs?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.observations = (reportData.observations || []).map((o) => (o._id === obs._id ? obs : o));
        syncCurrentReportData(state);
      })
      .addCase(updateFollowUpAction.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        const reportId = obs?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.observations = (reportData.observations || []).map((o) => (o._id === obs._id ? obs : o));
        syncCurrentReportData(state);
      })
      .addCase(resolveObservation.fulfilled, (state, action) => {
        const obs = withoutKeyIssues(action.payload);
        const reportId = obs?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) reportData.observations = (reportData.observations || []).map((o) => (o._id === obs._id ? obs : o));
        syncCurrentReportData(state);
      })
      .addCase(updateSiteData.fulfilled, (state, action) => {
        const siteDataPayload = action.payload;
        const siteData = siteDataPayload?.data || siteDataPayload;
        const reportId = siteData?.reportId || getActiveReportId();
        const reportData = ensureReportData(state, reportId);
        if (reportData) {
          // Section updates are nested patches, with or without a queue marker.
          reportData.siteOverview = mergeSiteData(reportData.siteOverview, siteData);
        }
        syncCurrentReportData(state);
      })
       .addCase(updateSiteOverviewData.fulfilled, (state, action) => {
         const overviewPayload = action.payload;
         const overview = overviewPayload?.data || overviewPayload;
         const reportId = overview?.reportId || getActiveReportId();
         const reportData = ensureReportData(state, reportId);
         if (reportData) reportData.siteOverview = overview;
         syncCurrentReportData(state);
       })
       .addCase(fetchReportWithAllData.rejected, (state, action) => {
        state.dataLoading = false;
        state.error = action.payload;
      })
      // Handle fetchAllReportsWithData
      .addCase(fetchAllReportsWithData.pending, (state) => {
        state.dataLoading = true;
        state.error = null;
      })
      .addCase(fetchAllReportsWithData.fulfilled, (state, action) => {
        state.dataLoading = false;
        const reportsData = mergeReportDataRecords([], action.payload || [], {}, knownReports(state))
          .map(record => mergePendingMatrix(record, state.allReportsData[record.report._id]));
        // Normalize array into object indexed by reportId
        const localRecords = Object.values(state.allReportsData || {}).filter((record) =>
          record.report?._offlineQueued && !reportsData.some((server) => server.report?._id === canonicalReport(state, record.report)?._id));
        state.allReportsData = {};
        [...reportsData, ...localRecords].forEach((reportData) => {
          if (reportData?.report?._id) {
            state.allReportsData[reportData.report._id] = reportData;
          }
        });
        syncCurrentReportData(state);
      })
      .addCase(fetchAllReportsWithData.rejected, (state, action) => {
        state.dataLoading = false;
        state.error = action.payload;
      })
      // Persist pending files lifecycle
      .addCase(persistPendingFilesForReport.pending, (state, action) => {
        const reportId = (action.meta && action.meta.arg && action.meta.arg.reportId) || getActiveReportId();
        state.pendingFilePersistenceByReportId = state.pendingFilePersistenceByReportId || {};
        state.pendingFilePersistenceByReportId[reportId] = true;
      })
      .addCase(persistPendingFilesForReport.fulfilled, (state, action) => {
        const reportId = (action.meta && action.meta.arg && action.meta.arg.reportId) || getActiveReportId();
        if (state.pendingFilePersistenceByReportId) delete state.pendingFilePersistenceByReportId[reportId];
        // If fulfilled payload contains cloned files, reducers for cloneFiles.fulfilled
        // will already have updated reportData.files and removed isImported entries
        if (Array.isArray(action.payload) && (action.payload || []).length === 0) {
          // no-op
        }
      })
      .addCase(persistPendingFilesForReport.rejected, (state, action) => {
        const reportId = (action.meta && action.meta.arg && action.meta.arg.reportId) || getActiveReportId();
        if (state.pendingFilePersistenceByReportId) delete state.pendingFilePersistenceByReportId[reportId];
      })
  },
});

export const {
  setRecentReports,
  setSiteReports,
  setCurrentReport,
  setCurrentReportData,
  clearCurrentReport,
  upsertReport,
  addOptimisticReport,
  addSyncedReport,
  removeReport,
  addReportData,
  addPendingImportedFilesToReport,
  removePendingImportedFileFromReport,
  updateCurrentReportFile,
  replaceSyncedReportFile,
} = auditReportSlice.actions;

export const selectCurrentReport = (state) => state?.auditReports?.currentReport || null;
export const selectCurrentReportData = (state) => state?.auditReports?.currentReportData || null;
export const selectAllReportsData = (state) => state?.auditReports?.allReportsData || {};
export const selectReportDataById = (reportId) => (state) =>
  state?.auditReports?.allReportsData?.[reportId] || null;
export const selectDataLoading = (state) => state?.auditReports?.dataLoading || false;
export const selectHasPendingFileChanges = (state) => {
  const reportId = getActiveReportId();
  return Boolean(state?.auditReports?.pendingFileChangesByReportId?.[reportId]);
};

export const selectIsPersistingPendingFiles = (state) => {
  const reportId = getActiveReportId();
  return Boolean(state?.auditReports?.pendingFilePersistenceByReportId?.[reportId]);
};

export const selectCurrentReportReadOnly = (state) => {
  const report = state?.auditReports?.currentReport;
  if (!report) return false;

  return Boolean(
    // report.savedAt ||
    //   report.status === "generated" ||
    //   report.status === "completed" ||
    //   report.status === "reviewed" ||
    //   report.status === "archived" ||
      // report.generatedPdf?.generated ||
      // report.generatedPdf?.fileUrl,
      report.status==="saved" || report.status==="generated"
  );
};

export default auditReportSlice.reducer;
